/**
 * GraphQL queries and helpers for Tezos Domains
 */
import config from "../config/tezos";
import type { HackProfile } from "../types/profile";
import { parseProfileFromData, resolvePrimary } from "../types/profile";

const GRAPHQL_URL = config.domainsGraphql;

async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const res = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0].message);
    return json.data;
}

// ── Bulk fetch all hack.tez subdomains with profile data ────────────

export interface SubdomainWithProfile {
    label: string;
    name: string;
    address: string | null;
    owner: string;
    data: Array<{ key: string; value: unknown }>;
    profile: HackProfile;
}

/** Fetch all subdomains under hack.{tld} with profile data (paginates 50 at a time) */
export async function getAllSubdomains(): Promise<SubdomainWithProfile[]> {
    const parent = `hack.${config.tld}`;
    const suffix = `.${parent}`;
    const items: Array<{
        name: string;
        address: string | null;
        owner: string;
        data: Array<{ key: string; value: unknown }>;
    }> = [];
    let after: string | null = null;
    // Cap at 20 pages (1000 domains) as a safety stop.
    for (let i = 0; i < 20; i++) {
        const data: {
            domains: {
                items: Array<{
                    name: string;
                    address: string | null;
                    owner: string;
                    data: Array<{ key: string; value: unknown }>;
                }>;
                pageInfo: { hasNextPage: boolean; endCursor: string | null };
            };
        } = await gql(
            `query AllSubdomains($parent: String!, $after: String) {
      domains(where: { name: { endsWith: $parent } }, first: 50, after: $after, order: { field: NAME, direction: ASC }) {
        items {
          name
          address
          owner
          data { key value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }`,
            { parent: suffix, after },
        );
        items.push(...data.domains.items);
        if (!data.domains.pageInfo.hasNextPage || !data.domains.pageInfo.endCursor) break;
        after = data.domains.pageInfo.endCursor;
    }
    return items.flatMap((d) => {
        const label = d.name.replace(suffix, "");
        if (label.includes(".")) return [];
        return [{
            label,
            name: d.name,
            address: d.address,
            owner: d.owner,
            data: d.data,
            profile: parseProfileFromData(d.data),
        }];
    });
}

/** Check if a subdomain of hack.tez is available */
export async function checkAvailability(label: string): Promise<boolean> {
    const name = `${label}.hack.${config.tld}`;
    const data = await gql<{ domain: { name: string } | null }>(
        `query CheckDomain($name: String!) {
      domain(name: $name) {
        name
        data { key value }
      }
    }`,
        { name },
    );
    return data.domain === null;
}

export interface SubdomainRecord {
    name: string;
    address: string | null;
    owner: string;
    data: Array<{ key: string; value: unknown }>;
    profile: HackProfile;
}

/** Domain record with profile data — used by profile pages */
export interface DomainRecord {
    name: string;
    owner: string;
    address: string | null;
    data: Array<{ key: string; value: unknown }>;
    profile: HackProfile;
    /** Raw gravatar hash from TED data (if set) */
    gravatar: string | null;
}

/** Fetch a full domain record by name, including owner and profile */
export async function getDomainRecord(name: string): Promise<DomainRecord | null> {
    const data = await gql<{
        domain: {
            name: string;
            owner: string;
            address: string | null;
            data: Array<{ key: string; value: unknown }>;
        } | null;
    }>(
        `query DomainRecord($name: String!) {
      domain(name: $name) {
        name
        owner
        address
        data { key value }
      }
    }`,
        { name },
    );
    if (data.domain === null) return null;
    const gravatar = data.domain.data.find((d) => d.key === "gravatar:hash")?.value;
    return {
        name: data.domain.name,
        owner: data.domain.owner,
        address: data.domain.address,
        data: data.domain.data,
        profile: parseProfileFromData(data.domain.data),
        gravatar: typeof gravatar === "string" ? gravatar : null,
    };
}
export async function getSubdomainsByOwner(ownerAddress: string): Promise<SubdomainRecord[]> {
    const data = await gql<{
        domains: {
            items: Array<{
                name: string;
                address: string | null;
                owner: string;
                data: Array<{ key: string; value: unknown }>;
            }>;
        };
    }>(
        // NAME ASC matters: without it TED returns an arbitrary order and every
        // "first owned domain" fallback in the app flips between calls.
        `query OwnerDomains($owner: Address!, $parent: String!) {
      domains(where: { owner: { equalTo: $owner }, name: { endsWith: $parent } }, order: { field: NAME, direction: ASC }) {
        items {
          name
          address
          owner
          data { key value }
        }
      }
    }`,
        { owner: ownerAddress, parent: `.hack.${config.tld}` },
    );
    return data.domains.items.map((d) => ({
        name: d.name,
        address: d.address,
        owner: d.owner,
        data: d.data,
        profile: parseProfileFromData(d.data),
    }));
}

/** Fetch profile data for a specific domain name */
export async function getDomainProfile(name: string): Promise<HackProfile | null> {
    const data = await gql<{
        domain: { data: Array<{ key: string; value: unknown }> } | null;
    }>(
        `query DomainProfile($name: String!) {
      domain(name: $name) {
        data { key value }
      }
    }`,
        { name },
    );
    if (data.domain === null) return null;
    return parseProfileFromData(data.domain.data);
}

/** Validate a subdomain label (lowercase alphanumeric + hyphens, 1-63 chars) */
export function validateLabel(label: string): { valid: boolean; error?: string } {
    if (label.length === 0) return { valid: false, error: "Name cannot be empty" };
    if (label.length < 3) return { valid: false, error: "Name must be at least 3 characters" };
    if (label.length > 63) return { valid: false, error: "Name must be 63 characters or fewer" };
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(label)) {
        return { valid: false, error: "Only lowercase letters, numbers, and hyphens allowed" };
    }
    return { valid: true };
}

// Reserved names that cannot be registered
const RESERVED_NAMES = new Set([
    "admin",
    "support",
    "help",
    "www",
    "mail",
    "ftp",
    "api",
    "app",
    "ns1",
    "ns2",
    "dns",
    "mx",
    "smtp",
    "imap",
    "pop",
    "ssh",
    "hack",
    "tez",
    "tezos",
    "test",
    "dev",
    "staging",
    "prod",
    "official",
    "bot",
    "system",
    "root",
    "null",
    "undefined",
]);

export function isReserved(label: string): boolean {
    return RESERVED_NAMES.has(label.toLowerCase());
}

/** Resolve a tz address to its tzkt profile alias (if any) */
export async function resolveAddressToAlias(address: string): Promise<string | null> {
    try {
        const res = await fetch(`${config.tzktApi}/v1/accounts/${address}`);
        if (!res.ok) return null;
        const data = await res.json();
        return data.alias ?? null;
    } catch {
        return null;
    }
}

/** Pick the primary record out of an owner's subdomains (null when they own none). */
export function pickPrimary(
    owner: string,
    subdomains: SubdomainRecord[],
): SubdomainRecord | null {
    // Sub-subdomains belong to a member, they are never the member.
    const topLevel = subdomains.filter(
        (d) => d.name === `${d.name.split(".")[0]}.hack.${config.tld}`,
    );
    const name = resolvePrimary(owner, topLevel);
    return name === null ? null : (topLevel.find((d) => d.name === name) ?? null);
}

/** Get the primary hack.tez subdomain owned by an address (if any) */
async function getPrimaryHackTezSubdomain(address: string): Promise<string | null> {
    const subs = await getSubdomainsByOwner(address);
    return pickPrimary(address, subs)?.name ?? null;
}

/**
 * Resolve the best available display name for a tz address.
 * Priority (best → fallback):
 *   1. Primary hack.tez subdomain
 *   2. Tezos Domains reverse record
 *   3. tzkt profile alias
 *   4. null (caller shows truncated address)
 */
export async function resolveDisplayName(address: string): Promise<string | null> {
    const [hackTez, domain, alias] = await Promise.allSettled([
        getPrimaryHackTezSubdomain(address),
        resolveAddressToDomain(address),
        resolveAddressToAlias(address),
    ]);
    const best = (r: PromiseSettledResult<string | null>) =>
        r.status === "fulfilled" ? r.value : null;
    return best(hackTez) ?? best(domain) ?? best(alias);
}
/** Fetch sub-subdomains (children) of a specific parent domain (paginates 50 at a time) */
export async function getSubSubdomains(parentName: string): Promise<SubdomainRecord[]> {
    const items: Array<{
        name: string;
        address: string | null;
        owner: string;
        data: Array<{ key: string; value: unknown }>;
    }> = [];
    let after: string | null = null;
    for (let i = 0; i < 20; i++) {
        const data: {
            domains: {
                items: Array<{
                    name: string;
                    address: string | null;
                    owner: string;
                    data: Array<{ key: string; value: unknown }>;
                }>;
                pageInfo: { hasNextPage: boolean; endCursor: string | null };
            };
        } = await gql(
            `query SubSubdomains($parent: String!, $after: String) {
      domains(where: { name: { endsWith: $parent } }, first: 50, after: $after, order: { field: NAME, direction: ASC }) {
        items {
          name
          address
          owner
          data { key value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }`,
            { parent: `.${parentName}`, after },
        );
        items.push(...data.domains.items);
        if (!data.domains.pageInfo.hasNextPage || !data.domains.pageInfo.endCursor) break;
        after = data.domains.pageInfo.endCursor;
    }
    // Filter out the parent domain itself (endsWith matches it too)
    return items
        .filter((d) => d.name !== parentName)
        .map((d) => ({
            name: d.name,
            address: d.address,
            owner: d.owner,
            data: d.data,
            profile: parseProfileFromData(d.data),
        }));
}

/** Reverse-resolve a tz address to its .tez domain name (if any) */
export async function resolveAddressToDomain(address: string): Promise<string | null> {
    const data = await gql<{ reverseRecord: { domain: { name: string } } | null }>(
        `query ReverseLookup($address: String!) {
      reverseRecord(address: $address) {
        domain {
          name
        }
      }
    }`,
        { address },
    );
    return data.reverseRecord?.domain?.name ?? null;
}
